import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronUp,
  CircleHelp,
  Download,
  Lock,
  Printer,
  Sparkles,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router";
import { ALLERGENS } from "../../../shared/constants";
import { NUTRIENTS } from "../../../shared/nutrition";
import {
  NUTRITION_MONTHLY_INCLUDED,
  NUTRITION_PRICES,
  type NutritionProductDto,
  type NutritionPurchase,
  type NutritionRunDto,
  type NutritionStatusDto,
  nutritionPrice,
  PUBLISH_ACCEPTANCE_TEXT,
} from "../../../shared/nutrition-addon";
import type { RecipeDto } from "../../../shared/recipes";
import { ApiRequestError, api } from "../../api/client";
import { useMenus } from "../../api/queries";
import { CHIPS, StatusChip } from "../../components/kit";
import { useToast } from "../../components/Toast";
import { Alert, ICON } from "../../components/ui";
import type { ShellContext } from "../AppShell";
import { ConfirmDialog } from "../menus/dialogs";
import { NUTRIENT_LABELS } from "../menus/LabelModal";
import { RecipeBlock } from "../menus/RecipeBlock";
import { AllergenSummary } from "./AllergenBanner";

// "Valori nutriționale" (paid add-on, owner 2026-10-28; docs/BAZA_INGREDIENTE.md). Locked until the one-time
// payment; then the AI proposes each product's recipe (ingredients + grams, mapped to the food database) and the
// values are computed from the database. Two modes per run: the restaurant confirms each product, or accepts
// publishing the estimates at once. Export CSV (Excel) and a printable page (PDF).

const day = (ms: number) =>
  new Date(ms).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" });
const fmt = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : String(n).replace(".", ",");
const allergenLabel = (code: string) => ALLERGENS.find(([c]) => c === code)?.[1] ?? code;

const STOP_TEXT: Record<string, string> = {
  no_credits: "Analizele AI disponibile s-au terminat. Cumpără un pachet sau completează manual (gratuit).",
  ai_restaurant_limit: "Ai folosit tot AI-ul inclus pentru restaurantul tău. Completează rețetele manual.",
  ai_account_not_verified: "Analiza automată pornește după ce confirmi adresa de e-mail a contului.",
  ai_unavailable: "Analiza automată nu este disponibilă acum. Încearcă mai târziu sau completează manual.",
  ai_failed: "Analiza nu a reușit acum. Încearcă din nou.",
  stopped_by_user: "Ai oprit analiza.",
};

export function NutritionPage() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const canEdit = membership.role !== "staff";
  // Back from Stripe: ?checkout=success|cancel. The purchase is recorded by the invoice.paid webhook, a few
  // seconds later, so the status is asked again until it shows (at most about a minute).
  const [params] = useSearchParams();
  const returned = params.get("checkout");
  const [since] = useState(Date.now);
  const status = useQuery({
    queryKey: ["nutrition", rid, "status"],
    queryFn: () =>
      api<{ status: NutritionStatusDto }>("GET", `/restaurants/${rid}/nutrition/status`).then(
        (r) => r.status,
      ),
    refetchInterval: (q) =>
      returned === "success" && Date.now() - since < 60_000 && !q.state.data?.active ? 3_000 : false,
  });
  const s = status.data;
  return (
    <>
      <div className="stack" style={{ gap: 6 }}>
        <h1 className="h1">Valori nutriționale</h1>
        <p className="page-sub" style={{ margin: 0, maxWidth: 720 }}>
          Informațiile cerute pe meniu de Ordinul ANPC 201/2022: ingrediente cu cantități, alergeni, aditivi
          și valori nutriționale la 100 g. AI-ul propune rețeta fiecărui produs; cifrele se calculează din
          baze de date alimentare, nu sunt inventate de AI.
        </p>
      </div>
      <AllergenSummary rid={rid} />
      {returned === "success" && (
        <Alert tone="success" live>
          {s?.active
            ? "Plata a fost primită. Factura vine pe e-mail."
            : "Plata a fost primită. Deblocăm valorile nutriționale în câteva secunde…"}
        </Alert>
      )}
      {returned === "cancel" && <Alert tone="info">Plata a fost anulată. Nu s-a încasat nimic.</Alert>}
      {status.isError && <Alert tone="error">{status.error.message}</Alert>}
      {s && !s.everPaid && (
        <Locked rid={rid} canEdit={canEdit && membership.role === "owner"} complimentary={s.complimentary} />
      )}
      {s?.everPaid && (
        <Unlocked rid={rid} status={s} canEdit={canEdit} isOwner={membership.role === "owner"} />
      )}
    </>
  );
}

function useCheckout(rid: string) {
  return useMutation({
    // The add-on and its renewal are bought with the publishing text accepted (owner 2026-11-03).
    mutationFn: (kind: NutritionPurchase) =>
      api<{ url: string }>("POST", `/restaurants/${rid}/billing/nutrition-checkout`, {
        kind,
        ...(kind === "addon" || kind === "renewal" ? { accept: true } : {}),
      }),
    onSuccess: ({ url }) => window.location.assign(url),
  });
}

function checkoutError(e: unknown): ReactNode {
  if (e instanceof ApiRequestError && e.code === "billing_profile_required")
    return (
      <>
        Completează întâi datele de facturare (firma sau persoana pe care se emite factura).{" "}
        <Link to="/app/billing#profile-h">Date de facturare</Link>
      </>
    );
  if (e instanceof ApiRequestError && e.status === 503)
    return "Plățile online nu sunt active acum. Încearcă mai târziu sau scrie-ne.";
  return e instanceof Error ? e.message : "Plata nu a putut porni. Încearcă din nou.";
}

/** The currency this restaurant pays in (Abonament și facturare); lei until its billing details say otherwise. */
function usePayCurrency(rid: string): "RON" | "EUR" {
  const billing = useQuery({
    queryKey: ["billing", rid],
    queryFn: () => api<{ payCurrency: "RON" | "EUR" }>("GET", `/restaurants/${rid}/billing`),
    staleTime: 5 * 60_000,
  });
  return billing.data?.payCurrency ?? "RON";
}

function Locked({ rid, canEdit, complimentary }: { rid: string; canEdit: boolean; complimentary: boolean }) {
  const checkout = useCheckout(rid);
  const price = nutritionPrice("addon", usePayCurrency(rid));
  const qc = useQueryClient();
  const [agreed, setAgreed] = useState(false);
  // A test account the owner marked: activated without payment (migration 0044).
  const free = useMutation({
    mutationFn: () => api("POST", `/restaurants/${rid}/nutrition/complimentary`, { accept: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nutrition", rid] }),
  });
  return (
    <section
      className="card"
      aria-labelledby="nu-lock-h"
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div className="row" style={{ gap: 10, alignItems: "center" }}>
        <Lock {...ICON} aria-hidden="true" />
        <h2 id="nu-lock-h" className="h3" style={{ margin: 0 }}>
          Deblochează valorile nutriționale
        </h2>
      </div>
      <ul style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 4 }}>
        <li>
          AI-ul propune rețeta fiecărui produs din meniu (ingrediente și gramaje, inclusiv din „250 g
          (100/60/40…)”).
        </li>
        <li>
          Calculăm valorile la 100 g și pe porție, alergenii, E-urile și „din produs congelat”, din baze de
          date.
        </li>
        <li>
          Totul apare imediat în meniu, marcat „generat de Meniova, neconfirmat de restaurant”. Apoi verifici
          produs cu produs și confirmi: marcajul dispare la fiecare produs confirmat.
        </li>
        <li>
          Meniul inițial e inclus un an; apoi 10 produse analizate cu AI pe lună. Editarea manuală e mereu
          gratuită.
        </li>
        <li>Export CSV (Excel) și PDF, ca să păstrezi valorile și după închiderea contului.</li>
      </ul>
      <p style={{ margin: 0 }}>
        <strong>{price}</strong> pentru un an, plătit o dată. Nu face parte din perioada gratuită.
      </p>
      {canEdit && (
        <label className="row" style={{ gap: 8, alignItems: "flex-start" }}>
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span className="caption">{PUBLISH_ACCEPTANCE_TEXT}</span>
        </label>
      )}
      {checkout.isError && <Alert tone="warning">{checkoutError(checkout.error)}</Alert>}
      {free.isError && <Alert tone="warning">{free.error.message}</Alert>}
      {canEdit && complimentary ? (
        <div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={free.isPending || !agreed}
            onClick={() => free.mutate()}
          >
            Activează (cont de test, fără plată)
          </button>
        </div>
      ) : canEdit ? (
        <div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={checkout.isPending || !agreed}
            onClick={() => checkout.mutate("addon")}
          >
            Deblochează — {NUTRITION_PRICES.addon.eur} €
          </button>
        </div>
      ) : (
        <p className="muted caption" style={{ margin: 0 }}>
          Doar proprietarul restaurantului poate face plata.
        </p>
      )}
    </section>
  );
}

function Unlocked({
  rid,
  status,
  canEdit,
  isOwner,
}: {
  rid: string;
  status: NutritionStatusDto;
  canEdit: boolean;
  isOwner: boolean;
}) {
  const menus = useMenus(rid);
  const renewal = nutritionPrice("renewal", usePayCurrency(rid));
  const list = (menus.data ?? []).filter((m) => !m.archived);
  const [menuId, setMenuId] = useState<string | null>(null);
  const mid = menuId ?? list[0]?.id ?? null;
  const checkout = useCheckout(rid);
  const left = Math.max(0, status.monthlyIncluded - status.monthlyUsed);
  return (
    <>
      <section
        className="card"
        aria-label="Abonament valori nutriționale"
        style={{ display: "flex", flexDirection: "column", gap: 10 }}
      >
        <div className="row" style={{ gap: 16, flexWrap: "wrap", alignItems: "baseline" }}>
          <strong>
            {status.active && status.endsAt
              ? `Activ până la ${day(status.endsAt)}`
              : "Anul inclus s-a încheiat"}
          </strong>
          {status.active && (
            <span className="muted">
              Analize AI rămase luna aceasta: {left} din {NUTRITION_MONTHLY_INCLUDED}
              {status.initialAvailable ? " · meniul inițial e inclus" : ""}
            </span>
          )}
          <span className="muted">Credite: {status.credits} analize</span>
        </div>
        {!status.active && (
          <p className="caption" style={{ margin: 0 }}>
            Valorile publicate rămân și le poți edita gratuit. Pentru 10 analize AI pe lună, reînnoiește cu{" "}
            {renewal} pe încă un an.
          </p>
        )}
        {isOwner && (
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {!status.active && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => checkout.mutate("renewal")}
              >
                Reînnoiește — {NUTRITION_PRICES.renewal.eur} €
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => checkout.mutate("pack5")}
            >
              20 de analize — 5 €
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => checkout.mutate("pack10")}
            >
              40 de analize — 10 €
            </button>
            <span className="caption muted" style={{ alignSelf: "center" }}>
              0,25 € pe produs analizat · creditul expiră după 12 luni · lei + TVA la plată
            </span>
          </div>
        )}
        {checkout.isError && <Alert tone="warning">{checkoutError(checkout.error)}</Alert>}
      </section>
      {list.length > 1 && (
        <div className="field" style={{ maxWidth: 360 }}>
          <label className="field-label" htmlFor="nu-menu">
            Meniul
          </label>
          <select
            id="nu-menu"
            className="field-input"
            value={mid ?? ""}
            onChange={(e) => setMenuId(e.target.value)}
          >
            {list.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {menus.isSuccess && !mid && (
        <section className="card empty">
          <p style={{ margin: 0 }}>Creează sau importă întâi un meniu.</p>
          <Link to="/app/imports">Import AI</Link>
        </section>
      )}
      {mid && <MenuNutrition rid={rid} mid={mid} canEdit={canEdit} alreadyAccepted={status.accepted} />}
    </>
  );
}

function MenuNutrition({
  rid,
  mid,
  canEdit,
  alreadyAccepted,
}: {
  rid: string;
  mid: string;
  canEdit: boolean;
  alreadyAccepted: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const base = `/restaurants/${rid}/nutrition`;
  const key = ["nutrition", rid, "menu", mid];
  const data = useQuery({
    queryKey: key,
    queryFn: () =>
      api<{ products: NutritionProductDto[]; run: NutritionRunDto | null }>(
        "GET",
        `${base}/menus/${mid}/products`,
      ),
  });
  // Owner 2026-11-03: estimates are always published at once, marked as Meniova's; the text is accepted once.
  const mode = "publish" as const;
  const [accepted, setAccepted] = useState(alreadyAccepted);
  const [run, setRun] = useState<NutritionRunDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [confirmAll, setConfirmAll] = useState(false);
  const looping = useRef(false);
  const current = run ?? data.data?.run ?? null;
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: key }),
      qc.invalidateQueries({ queryKey: ["nutrition", rid, "status"] }),
    ]);

  // The server analyses the menu by itself (it goes on when the page is closed); the page only follows it.
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );
  async function loop(r: NutritionRunDto) {
    if (looping.current) return;
    looping.current = true;
    let cur = r;
    try {
      while (cur.status === "running" && alive.current) {
        await new Promise((ok) => setTimeout(ok, 3_000));
        if (!alive.current) return;
        await refresh();
        const next = qc.getQueryData<{ run: NutritionRunDto | null }>(key)?.run;
        if (next?.id === cur.id) cur = next;
        setRun(cur);
      }
      if (cur.status === "done" && alive.current) toast("Analiza s-a încheiat.");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      looping.current = false;
    }
  }

  // A run still going when the page comes back is followed again.
  const openRun = data.data?.run ?? null;
  const resume = useRef(loop);
  resume.current = loop;
  useEffect(() => {
    if (canEdit && openRun?.status === "running" && !looping.current) void resume.current(openRun);
  }, [openRun, canEdit]);

  async function start(productIds?: string[]) {
    setError(null);
    try {
      const body = { mode, menuId: mid, acceptance: PUBLISH_ACCEPTANCE_TEXT, productIds };
      const r = (await api<{ run: NutritionRunDto }>("POST", `${base}/runs`, body)).run;
      setRun(r);
      await loop(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function stop() {
    if (!current) return;
    setRun((await api<{ run: NutritionRunDto }>("POST", `${base}/runs/${current.id}/stop`)).run);
  }

  async function verify(pid: string) {
    try {
      await api("POST", `${base}/menus/${mid}/products/${pid}/verify`);
      toast("Verificat. Valorile apar pe meniu ca estimate.");
      setOpen(null);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      await refresh();
    }
  }

  const products = data.data?.products ?? [];
  const unverified = products.filter((p) => p.aiUnverified).length;
  const withoutRecipe = products.filter((p) => !p.lines && !p.missing.length).length;
  const verifiable = products.filter(canVerify);
  const toSetUp = products.filter((p) => p.missing.length > 0 || (p.aiUnverified && p.lines > 0)).length;
  const counts = Object.fromEntries(
    FILTERS.map(([f]) => [f, products.filter(FILTER_TEST[f]).length]),
  ) as Record<Filter, number>;
  // A list that became empty (the last product completed) goes back to all products.
  const active: Filter = filter !== "all" && counts[filter] === 0 ? "all" : filter;
  const shown = products.filter(FILTER_TEST[active]);

  async function verifyAll() {
    const r = await api<{ verified: number; skipped: number }>("POST", `${base}/menus/${mid}/verify`, {
      productIds: verifiable.map((p) => p.id),
    });
    toast(
      `${r.verified === 1 ? "Un produs verificat" : `${r.verified} produse verificate`}. Valorile apar pe meniu ca estimate.`,
    );
    setConfirmAll(false);
    await refresh();
  }
  const running = current?.status === "running";
  return (
    <>
      {canEdit && (
        <section
          className="card"
          aria-labelledby="nu-run-h"
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <h2 id="nu-run-h" className="h3" style={{ margin: 0 }}>
            Analizează meniul cu AI
          </h2>
          <p style={{ margin: 0 }}>
            Rețetele, valorile, alergenii și E-urile apar imediat în meniu, marcate „generate de Meniova,
            neconfirmate de restaurant”. Apoi le verifici produs cu produs; fiecare confirmare scoate
            marcajul.
          </p>
          {!alreadyAccepted && (
            <label className="row" style={{ gap: 8, alignItems: "flex-start" }}>
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
              <span className="caption">{PUBLISH_ACCEPTANCE_TEXT}</span>
            </label>
          )}
          <p className="caption muted" style={{ margin: 0 }}>
            Se analizează produsele fără rețetă ({withoutRecipe}).
          </p>
          <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {running ? (
              <>
                <span aria-live="polite">
                  Se analizează… {current?.productsDone ?? 0} din {current?.productsTotal ?? 0} produse
                </span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={stop}>
                  Oprește
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                disabled={!accepted || withoutRecipe === 0}
                onClick={() => start()}
              >
                <Sparkles {...ICON} aria-hidden="true" /> Analizează cu AI
              </button>
            )}
          </div>
          {current && current.status !== "running" && (
            <Alert tone={current.status === "done" ? "success" : "warning"} live>
              {current.status === "done"
                ? `Ultima analiză: ${current.productsDone} produse${current.productsMissing ? `, ${current.productsMissing} cu ingrediente de completat` : ""}.`
                : (STOP_TEXT[current.stopReason ?? ""] ?? "Analiza s-a oprit.")}
            </Alert>
          )}
          {error && <Alert tone="error">{error}</Alert>}
        </section>
      )}

      {canEdit && toSetUp > 0 && !running && (
        <section
          className="card"
          aria-labelledby="nu-setup-h"
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          <h2 id="nu-setup-h" className="h3" style={{ margin: 0 }}>
            {toSetUp === 1 ? "Un produs are nevoie de tine" : `${toSetUp} produse au nevoie de tine`}
          </h2>
          <p className="caption muted" style={{ margin: 0 }}>
            AI-ul a propus rețetele. Te întrebăm, produs cu produs, doar ce nu e clar (ingrediente negăsite,
            preparate ca blatul de pizza) și confirmi. Poți sări peste oricare și reveni mai târziu.
          </p>
          <div>
            <Link className="btn btn-primary" to={`/app/nutrition/configurare?menu=${mid}`}>
              <Sparkles {...ICON} aria-hidden="true" /> Configurează produsele
            </Link>
          </div>
        </section>
      )}

      <section
        className="card"
        aria-labelledby="nu-list-h"
        style={{ display: "flex", flexDirection: "column", gap: 10 }}
      >
        <div
          className="row"
          style={{ gap: 8, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}
        >
          <h2 id="nu-list-h" className="h3" style={{ margin: 0 }}>
            Produse {unverified ? <span className="muted">· {unverified} de verificat</span> : null}
          </h2>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <a className="btn btn-secondary btn-sm" href={`/api${base}/menus/${mid}/export.csv`} download>
              <Download {...ICON} aria-hidden="true" /> CSV (Excel)
            </a>
            <Link className="btn btn-secondary btn-sm" to={`/app/nutrition/tiparire?menu=${mid}`}>
              <Printer {...ICON} aria-hidden="true" /> PDF
            </Link>
          </div>
        </div>
        <fieldset
          className="row"
          style={{ gap: 6, flexWrap: "wrap", alignItems: "center", border: 0, margin: 0, padding: 0 }}
        >
          <legend className="sr-only">Arată</legend>
          {FILTERS.filter(([f]) => f === "all" || counts[f] > 0).map(([f, text]) => (
            <button
              key={f}
              type="button"
              className={`btn btn-sm ${active === f ? "btn-primary" : "btn-secondary"}`}
              aria-pressed={active === f}
              onClick={() => setFilter(f)}
            >
              {text} ({counts[f]})
            </button>
          ))}
          {canEdit && verifiable.length > 0 && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ marginLeft: "auto" }}
              disabled={running}
              onClick={() => setConfirmAll(true)}
            >
              <Check {...ICON} aria-hidden="true" /> Verifică toate cele complete ({verifiable.length})
            </button>
          )}
        </fieldset>
        {data.isError && <Alert tone="error">{data.error.message}</Alert>}
        {!shown.length && products.length > 0 && (
          <p className="muted caption" style={{ margin: 0 }}>
            Niciun produs în această listă.
          </p>
        )}
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
          {shown.map((p) => (
            <li key={p.id} style={{ borderTop: "1px solid var(--line, #e5e5e5)", padding: "10px 0" }}>
              <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  aria-expanded={open === p.id}
                  aria-label={`Rețeta pentru ${p.name}`}
                  onClick={() => setOpen(open === p.id ? null : p.id)}
                >
                  {open === p.id ? (
                    <ChevronUp {...ICON} aria-hidden="true" />
                  ) : (
                    <ChevronDown {...ICON} aria-hidden="true" />
                  )}
                </button>
                <span style={{ flex: "1 1 200px", minWidth: 0 }}>
                  <strong style={{ fontWeight: 600 }}>{p.name}</strong>
                  <span className="muted caption"> · {p.categoryName}</span>
                </span>
                <span className="muted" style={{ minWidth: 110 }}>
                  {p.kcalPer100 !== null ? `${fmt(p.kcalPer100)} kcal / 100 g` : "—"}
                </span>
                {p.aiUnverified && <StatusChip {...CHIPS.aiUnverified} />}
                {p.missing.length > 0 && <StatusChip tone="warning" icon={CircleHelp} label="De completat" />}
                {!p.lines && !p.missing.length && <StatusChip tone="neutral" icon={X} label="Fără rețetă" />}
                {p.published && (
                  <StatusChip
                    tone="success"
                    icon={Check}
                    label={p.published === "verified" ? "Pe meniu · verificat" : "Pe meniu · estimat"}
                  />
                )}
              </div>
              {open === p.id && (
                <ProductRecipe
                  rid={rid}
                  mid={mid}
                  product={p}
                  canEdit={canEdit}
                  busy={running}
                  onVerify={() => verify(p.id)}
                  onReanalyse={() => start([p.id])}
                  onChanged={refresh}
                />
              )}
            </li>
          ))}
        </ul>
      </section>
      {confirmAll && (
        <ConfirmDialog
          title={`Verifici ${verifiable.length === 1 ? "un produs" : `${verifiable.length} produse`}?`}
          body="Confirmi că ai citit rețetele lor (ingrediente și gramaje). Valorile apar pe meniul public ca „valori estimate”, verificate de tine. Le poți corecta oricând. Alergenii nu se confirmă aici."
          confirmLabel="Verifică și publică valorile"
          danger={false}
          onClose={() => setConfirmAll(false)}
          onConfirm={verifyAll}
        />
      )}
    </>
  );
}

type Filter = "all" | "missing" | "unverified" | "none" | "published";
const canVerify = (p: NutritionProductDto) =>
  p.lines > 0 && !p.missing.length && (p.aiUnverified || p.published !== "verified");
const FILTERS: [Filter, string][] = [
  ["all", "Toate"],
  ["missing", "De completat"],
  ["unverified", "De verificat"],
  ["none", "Fără rețetă"],
  ["published", "Pe meniu"],
];
const FILTER_TEST: Record<Filter, (p: NutritionProductDto) => boolean> = {
  all: () => true,
  missing: (p) => p.missing.length > 0,
  unverified: (p) => p.aiUnverified && !p.missing.length && p.lines > 0,
  none: (p) => !p.lines && !p.missing.length,
  published: (p) => !!p.published,
};

function ProductRecipe({
  rid,
  mid,
  product,
  canEdit,
  busy,
  onVerify,
  onReanalyse,
  onChanged,
}: {
  rid: string;
  mid: string;
  product: NutritionProductDto;
  canEdit: boolean;
  busy: boolean;
  onVerify: () => void;
  onReanalyse: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const recipe = useQuery({
    queryKey: ["recipe", rid, product.id, product.lines, product.verifiedAt],
    queryFn: () =>
      api<{ recipe: RecipeDto }>(
        "GET",
        `/restaurants/${rid}/menus/${mid}/products/${product.id}/recipe`,
      ).then((r) => r.recipe),
    // Editors change the recipe right here (usability audit 2026-11-02); the others only read it.
    enabled: !canEdit,
  });
  const r = recipe.data;
  const n = r?.nutrition ?? null;
  if (canEdit)
    return (
      <div
        style={{
          padding: "4px 0 4px clamp(0px, 5vw, 40px)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <RecipeBlock
          rid={rid}
          mid={mid}
          pid={product.id}
          shareMissing
          onSaved={() => void onChanged()}
          onBaseSaved={() => void onChanged()}
        />
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          {canVerify(product) && (
            <button type="button" className="btn btn-primary btn-sm" onClick={onVerify}>
              <Check {...ICON} aria-hidden="true" /> Am verificat · publică valorile
            </button>
          )}
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={onReanalyse}>
            <Sparkles {...ICON} aria-hidden="true" />{" "}
            {product.lines ? "Reanalizează cu AI" : "Analizează cu AI"}
          </button>
        </div>
      </div>
    );
  return (
    <div
      style={{ padding: "8px 0 4px clamp(0px, 5vw, 40px)", display: "flex", flexDirection: "column", gap: 8 }}
    >
      {r && r.lines.length > 0 && (
        <p style={{ margin: 0 }}>
          {r.lines.map((l) => `${l.name} ${fmt(l.grams)} g`).join(" · ")}
          {r.cookedGrams ? <span className="muted"> · porție servită {fmt(r.cookedGrams)} g</span> : null}
        </p>
      )}
      {r && r.missing.length > 0 && (
        <Alert tone="warning" title="De completat">
          {r.missing.join(", ")} — nu sunt încă în baza de date.
        </Alert>
      )}
      {n && (
        <p className="caption" style={{ margin: 0 }}>
          La 100 g: {NUTRIENTS.map((k) => `${NUTRIENT_LABELS[k]} ${fmt(n.per100g[k])}`).join(" · ")}
          {n.allergens.length ? ` · alergeni din rețetă: ${n.allergens.map(allergenLabel).join(", ")}` : ""}
          {n.additives.length ? ` · E-uri: ${n.additives.join(", ")}` : ""}
          {n.frozen ? " · din produs congelat" : ""}
        </p>
      )}
    </div>
  );
}
